import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { AgGridReact } from 'ag-grid-react';
import AppContext from 'AppContext';
import GrpButton from 'Library/GrpButton/GrpButton';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import { getScopes, deleteScope, duplicateScope, uploadDistributionFile } from 'Services/distributionApi';
import classes from './ScopesList.module.scss';

const ScopesList = () => {
  const context = useContext(AppContext);
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const fileInputRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, scope: null });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getScopes();
      const data = result.value || result.Content || [];
      setRowData(data);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load scopes'}</> });
    } finally {
      setLoading(false);
    }
  }, [context.handlers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Close context menu on outside click
  useEffect(() => {
    const handleClick = () => setContextMenu((prev) => ({ ...prev, visible: false }));
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const columnDefs = [
    {
      headerName: 'Scope Name',
      field: 'ScopeName',
      filter: 'agTextColumnFilter',
      flex: 1,
      cellRenderer: (params) => (
        <span
          className={classes.navLink}
          onClick={() => navigate(`/distribution/scope/${params.data.ScopeId}`)}
        >
          {params.value}
        </span>
      ),
    },
    {
      headerName: 'Actions',
      width: 90,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <GrpButton
          size="Small"
          icon={['fas', 'ellipsis-vertical']}
          iconSide="Round"
          onClick={(e) => {
            e.stopPropagation();
            setContextMenu({
              visible: true,
              x: e.clientX,
              y: e.clientY,
              scope: params.data,
            });
          }}
        />
      ),
    },
  ];

  const handleEdit = () => {
    if (contextMenu.scope) {
      navigate(`/distribution/scope/${contextMenu.scope.ScopeId}`);
    }
    setContextMenu((prev) => ({ ...prev, visible: false }));
  };

  const handleDelete = async () => {
    const scope = contextMenu.scope;
    setContextMenu((prev) => ({ ...prev, visible: false }));
    if (!scope) return;

    try {
      setLoading(true);
      await deleteScope(scope.ScopeId);
      context.handlers.setToast({ type: 'success', body: <>{`Scope "${scope.ScopeName}" deleted`}</> });
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to delete scope'}</> });
      setLoading(false);
    }
  };

  const handleDuplicate = async () => {
    const scope = contextMenu.scope;
    setContextMenu((prev) => ({ ...prev, visible: false }));
    if (!scope) return;

    try {
      setLoading(true);
      await duplicateScope(scope);
      context.handlers.setToast({ type: 'success', body: <>Scope duplicated successfully</> });
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to duplicate scope'}</> });
      setLoading(false);
    }
  };

  const handleCreateScope = () => {
    navigate('/distribution/scope');
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      await uploadDistributionFile(file, 'Scope');
      // Allow backend processing time
      await new Promise((resolve) => setTimeout(resolve, 2000));
      context.handlers.setToast({ type: 'success', body: <>File uploaded successfully</> });
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to upload file'}</> });
      setLoading(false);
    } finally {
      // Reset file input so same file can be re-uploaded
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return <GrpSpinner />;
  }

  return (
    <div className={classes.container}>
      <div className={classes.toolbar}>
        <span className={classes.spacer} />
        <GrpButton
          size="Medium"
          type="Primary"
          text="Create Scope"
          onClick={handleCreateScope}
        />
        <GrpButton
          size="Medium"
          type="Secondary"
          text="Import Scope"
          onClick={handleImportClick}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>

      <div className={`ag-theme-reborn ${classes.gridWrapper}`}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
          }}
          domLayout="autoHeight"
        />
      </div>

      {contextMenu.visible && (
        <div
          className={classes.contextMenu}
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <div className={classes.menuItem} onClick={handleEdit}>Edit</div>
          <div className={classes.menuItem} onClick={handleDelete}>Delete</div>
          <div className={classes.menuItem} onClick={handleDuplicate}>Duplicate</div>
        </div>
      )}
    </div>
  );
};

export default ScopesList;
